import React from 'react';

import { CardControlPanel as CardControlPanelV3 } from '@eika-kortbetjening/card-control-panel';
import { ColumnDividerBox } from '@eika-kortbetjening/column-divider-box';
import { DetailList } from '@eika-kortbetjening/detail-list';
import { Loader } from '@eika/loader';
import { ModalDialogProps } from '@eika/modal';

import { useSession } from '@/components/app';
import { KbjCardService } from '@/domain/card';
import piwik from '@/piwik/piwik';
import { useAccount } from '@/react-query/hooks/query/use-account';
import { useCustomer } from '@/react-query/hooks/query/use-customer';

import getDetailListItems from '@components/card/card-details/details';
import { Card, DebitAccount, Organisation, User } from '@generated-types';
import useText from '@hooks/use-text';
import { getBankCardName } from '@utils/card-utils';
import EikaModal from '@utils/modal/EikaModal';

interface CardDetailsModalContentProps {
    card: Card;
    account: DebitAccount;
    customer: User | Organisation;
}

const CardDetailsModalContent = ({ card, account, customer }: CardDetailsModalContentProps) => {
    const text = useText();
    const session = useSession();

    const detailListItems = getDetailListItems(card, customer, account);

    const [kbjCard, kbjCardMeta] = KbjCardService.toKbjPanel(
        card,
        session,
        getBankCardName(card, text, account),
        () => '',
    );

    if (!detailListItems) {
        return null;
    }

    return (
        <ColumnDividerBox>
            <ColumnDividerBox.Item>
                <CardControlPanelV3 card={kbjCard} cardMeta={kbjCardMeta} sitecoreDictionary={text} piwik={piwik} />
            </ColumnDividerBox.Item>
            {detailListItems.map((item) => (
                <ColumnDividerBox.Item key={item.id || item.value?.toString()}>
                    <DetailList.Item description={item.description} value={item.value} />
                </ColumnDividerBox.Item>
            ))}
        </ColumnDividerBox>
    );
};

interface CardDetailsModalProps extends ModalDialogProps {
    card: Card;
}

const CardDetailsModal = (props: CardDetailsModalProps) => {
    const { cardLinkCardDetails } = useText();
    const accountQuery = useAccount(props.card);
    const customerQuery = useCustomer(props.card);

    if (accountQuery.isPending || accountQuery.isError || customerQuery.isPending || customerQuery.isError) {
        return (
            <EikaModal title={cardLinkCardDetails} {...props}>
                <Loader />
            </EikaModal>
        );
    }

    return (
        <EikaModal title={cardLinkCardDetails} {...props}>
            <CardDetailsModalContent card={props.card} account={accountQuery.data} customer={customerQuery.data} />
        </EikaModal>
    );
};

export default CardDetailsModal;
